/**
 * Cart upgrade offer (snippets/cart-upgrade-offer.liquid): swaps a cart line
 * for the upgrade product its product names in custom.upgrade_products.
 *
 * Two requests, one repaint. /cart/add.js puts the upgrade in first, so the
 * cart never passes through empty (which would reset the declined-gift
 * list); then CartItems.removeLines takes the original out, carrying the
 * tracking attributes and the rendered sections in that same /cart/update.js
 * call. removeLines settles the free gifts from the response's real totals
 * before painting, so a tier the new total earns or loses lands in the same
 * paint. Its usual projection of which gifts a removal costs is switched off
 * here: it works from the pre-add DOM and would call the swap a loss.
 *
 * Tags the new line with _upsell_type=upgrade_upsell and _upgraded_from (the
 * original product's handle, so cart-upsell-dependencies lets the new line
 * vouch for the add-ons the old one brought), and the cart with
 * _upsell_used/_upsell_types, the attributes cart-upsell-tracking.js writes
 * for the carousels.
 *
 * Relies on cart.js globals: routes, fetchConfig, splitKeys, and the
 * cart-items / cart-drawer-items element this sits inside.
 */
class CartUpgradeOffer extends HTMLElement {
  connectedCallback() {
    this._button = this.querySelector('[data-role="upgrade"]');
    if (!this._button) return;
    this._button.addEventListener('click', () => this.upgrade());

    // The variant pick (colour and the like, where several upgrade variants
    // fit the line): the chosen option carries the variant id the swap
    // sends, the image for that version of the new product, and its variant
    // title for the checklist. Prices are the same across the candidates,
    // being one product, so the button's figure stands.
    this._select = this.querySelector('[data-role="variant"]');
    if (this._select) this._select.addEventListener('change', () => this._pick());
  }

  _pick() {
    const option = this._select.selectedOptions[0];
    if (!option) return;
    this.dataset.variantId = option.value;
    const image = this.querySelector('.cart-gift-teaser__media img');
    if (image && option.dataset.image) {
      image.removeAttribute('srcset');
      image.src = option.dataset.image;
    }
    const row = this.querySelector('.cart-upgrade-offer__components .is-new');
    const note = row && row.querySelector('small');
    if (note) note.textContent = option.dataset.note || '';

    // A pick that swaps the new product itself (a flavour sold as its own
    // product) renames it: in the checklist, the title and the button, where
    // the name sits in text the settings templates wrote around it.
    const name = option.dataset.name;
    const previous = this._pickedName || (this._select.querySelector('option[selected]') || {}).dataset?.name;
    if (name && previous && name !== previous) {
      const nameNode = row && row.querySelector('.cart-upgrade-offer__component-name');
      if (nameNode) nameNode.firstChild.textContent = name;
      ['.cart-gift-teaser__title a', '[data-role="upgrade"] > span'].forEach((selector) => {
        const element = this.querySelector(selector);
        if (!element) return;
        element.childNodes.forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE && node.textContent.includes(previous)) {
            node.textContent = node.textContent.replace(previous, name);
          }
        });
      });
    }
    this._pickedName = name || previous;
  }

  async upgrade() {
    if (this._busy) return;
    this._busy = true;
    this._setLoading(true);

    const container = this.closest('cart-drawer-items, cart-items');
    const item = {
      id: Number(this.dataset.variantId),
      quantity: Number(this.dataset.quantity) || 1,
      properties: {
        _upsell_type: 'upgrade_upsell',
        _upgraded_from: this.dataset.fromHandle || '',
      },
    };
    const sellingPlan = Number(this.dataset.sellingPlan);
    if (sellingPlan) item.selling_plan = sellingPlan;

    // kaching_bundles=true is Kaching's own "already merged" marker; without
    // it a Kaching deal block on the page can rewrite a subscription add and
    // drop the selling plan. See product-form.js for the full story.
    const addUrl =
      sellingPlan && window.routes
        ? `${routes.cart_add_url}${routes.cart_add_url.includes('?') ? '&' : '?'}kaching_bundles=true`
        : routes.cart_add_url;

    try {
      const added = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] }),
      }).then((response) => response.json());

      if (added.status) {
        throw new Error(added.description || added.message || 'Could not add the upgrade.');
      }

      const types = splitKeys(this.dataset.upsellTypes);
      if (!types.includes('upgrade_upsell')) types.push('upgrade_upsell');
      const attributes = { _upsell_used: 'true', _upsell_types: types.join(',') };

      if (container && typeof container.removeLines === 'function') {
        await container.removeLines([this.dataset.lineKey], {
          decline: false,
          projectGifts: false,
          attributes,
        });
        // A successful removal repaints the cart, replacing this element.
        return;
      }

      // No cart container to hand the removal to (should not happen: the
      // card only renders inside one). Fall back to a plain removal and a
      // full refresh of the cart sections.
      await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify({ updates: { [this.dataset.lineKey]: 0 }, attributes }),
      });
      if (typeof refreshCartSections === 'function') await refreshCartSections();
    } catch (error) {
      console.error('[cart-upgrade-offer]', error);
      const errors =
        document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
      if (errors) errors.textContent = (window.cartStrings && window.cartStrings.error) || error.message;
      this._setLoading(false);
      this._busy = false;
    }
  }

  _setLoading(isLoading) {
    if (!this._button) return;
    this._button.classList.toggle('loading', isLoading);
    this._button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
    const spinner = this._button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }
}

if (!customElements.get('cart-upgrade-offer')) {
  customElements.define('cart-upgrade-offer', CartUpgradeOffer);
}
